import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, PauseCircle, Pencil, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import {
  AlertasPresupuesto,
  TotalesPresupuesto,
} from "@/componentes/presupuesto/TotalesPresupuesto"
import { monto as comoMonto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import {
  consultaDe,
  nombrePeriodo,
  type PeriodoAdmin,
  periodoInicial,
  SelectorPeriodoAdmin,
} from "@/componentes/ui/SelectorPeriodoAdmin"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo, type CategoriaGasto } from "@/paginas/interno/Gastos"

type EstadoPartida = {
  id: number
  categoria_id: number
  categoria: string
  tope: string
  gastado: string
  comprometido: string
  usado: string
  disponible: string
  porcentaje: number
  excedida: boolean
  ordenes: number
  notas: string | null
  /** A partir de qué porcentaje avisa (12/10). */
  umbral: number
  alerta: "UMBRAL" | "EXCEDIDA" | null
}

export type Estado = {
  anio: number
  mes: number
  partidas: EstadoPartida[]
  tope: string
  usado: string
  excedidas: number
  fuera_de_partida: string
  ordenes_fuera: number
  comprometido_fuera: string
  ejecutado_dentro: string
  comprometido_dentro: string
  ejecutado_total: string
  saldo: string
  en_alerta: number
  sin_convertir: { codigo: string; divisa: string; monto: string; motivo: string }[]
}

/** El color de la barra según cómo va. Verde no es «bien»: es «queda holgura». */
function tono(p: EstadoPartida): string {
  if (p.excedida) return "var(--destructive)"
  if (p.porcentaje >= p.umbral) return "var(--warning)"
  return "var(--success)"
}

/** Las partidas como documento exportable: aquí son tarjetas, no una `<table>`. */
function documentoPresupuesto(datos: Estado, periodo: string): DocumentoExportable {
  const n = (t: string) => Number(t)
  return {
    titulo: "Presupuesto",
    subtitulo: `Período: ${periodo} · en dólares, con la tasa del día de cada orden`,
    secciones: [
      {
        titulo: "Partidas del período",
        columnas: [
          { titulo: "Partida", tipo: "texto" },
          { titulo: "Tope (USD)", tipo: "monto" },
          { titulo: "Pagado (USD)", tipo: "monto" },
          { titulo: "Comprometido (USD)", tipo: "monto" },
          { titulo: "Usado (USD)", tipo: "monto" },
          { titulo: "Disponible (USD)", tipo: "monto" },
          { titulo: "% usado", tipo: "numero" },
          { titulo: "Órdenes", tipo: "numero" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: datos.partidas.map((p) => ({
          celdas: [
            p.categoria,
            n(p.tope),
            n(p.gastado),
            n(p.comprometido),
            n(p.usado),
            n(p.disponible),
            p.porcentaje,
            p.ordenes,
            p.excedida ? "Excedida" : p.porcentaje >= p.umbral ? "En alerta" : "Con holgura",
          ],
        })),
        pie: [
          [
            "Total dentro de partida",
            n(datos.tope),
            n(datos.ejecutado_dentro),
            n(datos.comprometido_dentro),
            n(datos.usado),
            n(datos.saldo),
            null,
            null,
            datos.excedidas > 0 ? `${datos.excedidas} excedida(s)` : null,
          ],
          [
            "Fuera de partida",
            null,
            n(datos.fuera_de_partida),
            n(datos.comprometido_fuera),
            null,
            null,
            null,
            datos.ordenes_fuera,
            null,
          ],
        ],
      },
    ],
  }
}

/**
 * Presupuesto: el tope del mes por categoría de gasto, y cómo va.
 *
 * ⚠ **AVISA, NO BLOQUEA.** Pasarse de una partida no impide redactar, aprobar ni pagar
 * nada: se pinta en rojo y se dice de cuánto. Un tope que frenara el circuito obligaría
 * a inventar un permiso para saltárselo —y a usarlo cada vez que la luz sube—, y el día
 * que estorbara se apagaría entero.
 *
 * ⚠ **Todo en DÓLARES, con la tasa del día de emisión de cada orden**, igual que el
 * informe de gastos. Un tope que mezclara bolívares con euros no se podría comparar con
 * nada, y convertir con la tasa de hoy haría que lo gastado en agosto cambiara cada
 * mañana sin moverse un céntimo.
 *
 * ⚠ **«Usado» es lo ejecutado MÁS lo comprometido**, que es con lo que hay que contar
 * para decidir si queda. Lo que está en borrador todavía no suma: se edita y se anula
 * sin que nadie lo firme —y es justo donde nacen los gastos programados—.
 *
 * ⚠ **Lo que cae fuera de toda partida se enseña aparte y no se esconde.** Sin eso,
 * «el presupuesto va bien» podría decirse con medio gasto del mes fuera de control.
 */
export function Presupuesto() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  // ⚠ **Semanal por omisión, con 15, 30, 60 días o el mes** (28/10, minuta punto 6). El
  // tope mensual de cada partida se prorratea a los días del período en el servidor.
  const [periodo, setPeriodo] = useState<PeriodoAdmin>(periodoInicial)
  const filtro = consultaDe(periodo)
  const [creando, setCreando] = useState(false)
  const [abierta, setAbierta] = useState<EstadoPartida | null>(null)
  // ⚠ **Ningún indicador se queda sin poder abrirse.** Una cifra que no se puede
  // pulsar obliga a adivinar qué hay detrás, y entonces deja más dudas de las que
  // aclara: «2.854,34 fuera de partida» puede ser gasto que nadie presupuestó, una
  // categoría nueva o un error de clasificación, y las tres se arreglan distinto.
  const [verFuera, setVerFuera] = useState(false)
  const [verTodo, setVerTodo] = useState(false)
  // ⚠ **Desde el presupuesto se puede abrir la orden**, y es el final del camino que
  // empieza en la tarjeta: cifra → qué la compone → el documento. Sin esto, la última
  // pregunta —«¿y qué dice esa orden?»— obligaba a copiar el código e irse a la bandeja.
  const [ordenAbierta, setOrdenAbierta] = useState<number | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "gasto:administrar")

  const estado = useQuery({
    queryKey: ["presupuesto", filtro],
    queryFn: () => api.get<Estado>(`/api/v1/presupuesto/estado?${filtro}`),
  })

  if (estado.isPending) return <p className="py-6">Cargando…</p>
  if (estado.isError || !estado.data) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudo cargar el presupuesto.
      </p>
    )
  }

  const datos = estado.data
  const mes = nombrePeriodo(periodo)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SelectorPeriodoAdmin valor={periodo} onCambiar={setPeriodo} />
        <div className="flex flex-wrap items-start gap-2">
          <Exportar titulo="Presupuesto" documento={() => documentoPresupuesto(datos, mes)} />
          {puedeAdministrar && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} /> Nueva partida
            </Boton>
          )}
        </div>
      </div>

      <TotalesPresupuesto
        datos={datos}
        mes={mes}
        onVerPartidas={() => setVerTodo(true)}
        onVerFuera={() => setVerFuera(true)}
      />

      <AlertasPresupuesto partidas={datos.partidas} />

      {datos.partidas.length === 0 ? (
        <p className="tarjeta p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no hay partidas. Una partida pone el tope del mes a una categoría —«suministros de
          oficina, 400 al mes»— y aquí se ve cuánto va consumido. No frena ningún pago: avisa.
        </p>
      ) : (
        <ul className="space-y-2">
          {datos.partidas.map((p) => (
            <li key={p.id} className="tarjeta p-0">
              {/* ⚠ **La fila entera se pulsa.** Una cifra que va al 90 % y no se puede
                  abrir obliga a buscar a mano en la bandeja filtrando por categoría y
                  por mes — justo el trabajo que la tarjeta venía a ahorrar. */}
              <button
                type="button"
                onClick={() => setAbierta(p)}
                className="foco-cristal w-full p-3 text-left"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="text-sm font-semibold">{p.categoria}</span>
                  <span className="flex items-center gap-2 text-sm tabular-nums">
                    <span>
                      {comoMonto(p.usado)}{" "}
                      <span style={{ color: "var(--fg-muted)" }}>de {comoMonto(p.tope)}</span>
                    </span>
                  </span>
                </div>
                {/* La barra es decorativa: la cifra de al lado es la que se lee. Por eso
                  va `aria-hidden` y el porcentaje se dice con palabras debajo. */}
                <div
                  aria-hidden={true}
                  className="mt-2 h-2 w-full overflow-hidden rounded-full"
                  style={{ backgroundColor: "var(--cristal-relleno)" }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(p.porcentaje, 100)}%`,
                      backgroundColor: tono(p),
                    }}
                  />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums">
                  <span style={{ color: "var(--fg-muted)" }}>
                    {p.porcentaje}% · pagado {comoMonto(p.gastado)} · comprometido{" "}
                    {comoMonto(p.comprometido)} · {p.ordenes}{" "}
                    {p.ordenes === 1 ? "orden" : "órdenes"}
                  </span>
                  {p.excedida ? (
                    <Insignia tono="peligro">
                      Excedida en {comoMonto(String(Math.abs(Number(p.disponible))))}
                    </Insignia>
                  ) : (
                    <Insignia tono={p.porcentaje >= p.umbral ? "aviso" : "exito"}>
                      Quedan {comoMonto(p.disponible)}
                    </Insignia>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {datos.sin_convertir.length > 0 && (
        <p className="tarjeta p-3 text-sm" style={{ color: "var(--warning)" }}>
          {datos.sin_convertir.length} orden(es) no se pudieron convertir a dólares y quedaron fuera
          de la cuenta: {datos.sin_convertir.map((s) => `${s.codigo} (${s.motivo})`).join(", ")}.
        </p>
      )}

      {/* El informe de gastos se fue a Administración → «Reportes» (28/10, minuta punto 2):
          una sola entrada para los reportes gerenciales. */}

      {/* La misma ficha que la bandeja, sin ejecutar: el dinero sale por un solo sitio. */}
      {ordenAbierta !== null && (
        <FichaOrden ordenId={ordenAbierta} onCerrar={() => setOrdenAbierta(null)} />
      )}

      <Modal
        abierto={verFuera}
        titulo={`Gasto fuera de partida · ${mes}`}
        descripcion="Lo ejecutado ese mes en categorías que ninguna partida gobierna. No es un error: es lo que todavía no tiene techo."
        onCerrar={() => setVerFuera(false)}
        ancho="lg"
      >
        {verFuera && <FueraDePartida filtro={filtro} onAbrir={setOrdenAbierta} />}
      </Modal>

      <Modal
        abierto={verTodo}
        titulo={`Las partidas de ${mes}`}
        descripcion="Cada tope con lo que lleva consumido. Pulsa una para ver qué órdenes la movieron."
        onCerrar={() => setVerTodo(false)}
        ancho="lg"
      >
        {verTodo && (
          <ul className="space-y-1 text-sm">
            {datos.partidas.map((p) => (
              <li key={p.id} className="flex flex-wrap justify-between gap-2 tabular-nums">
                <span>{p.categoria}</span>
                <span>
                  {comoMonto(p.usado)}{" "}
                  <span style={{ color: "var(--fg-muted)" }}>de {comoMonto(p.tope)}</span>{" "}
                  <Insignia
                    tono={p.excedida ? "peligro" : p.porcentaje >= p.umbral ? "aviso" : "exito"}
                  >
                    {p.porcentaje}%
                  </Insignia>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        abierto={abierta !== null}
        titulo={abierta ? `Qué consumió «${abierta.categoria}»` : "Detalle de la partida"}
        descripcion="Las órdenes de esa categoría ese mes, con su importe original y lo que aportaron en dólares."
        onCerrar={() => setAbierta(null)}
        ancho="lg"
      >
        {abierta !== null && (
          <DetallePartida
            partida={abierta}
            filtro={filtro}
            puedeAdministrar={puedeAdministrar}
            onAbrir={setOrdenAbierta}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["presupuesto"] })
              setAbierta(null)
            }}
          />
        )}
      </Modal>

      <Modal
        abierto={creando}
        titulo="Nueva partida"
        descripcion="El tope del mes para una categoría, en dólares. Avisa cuando se pasa; no frena ningún pago."
        onCerrar={() => setCreando(false)}
      >
        {creando && (
          <FormularioPartida
            onCerrar={() => setCreando(false)}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["presupuesto"] })
              setCreando(false)
            }}
          />
        )}
      </Modal>
    </section>
  )
}

function FormularioPartida({
  onCerrar,
  onGuardado,
}: {
  onCerrar: () => void
  onGuardado: () => void
}) {
  const [categoriaId, setCategoriaId] = useState<number | null>(null)

  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post("/api/v1/presupuesto/partidas", cuerpo),
    onSuccess: onGuardado,
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const notas = String(datos.get("notas") ?? "").trim()
    guardar.mutate({
      categoria_id: categoriaId,
      monto_mensual: decimalOpcional(String(datos.get("monto_mensual") ?? "")),
      notas: notas === "" ? null : notas,
      umbral_aviso: Number(datos.get("umbral_aviso") ?? 80),
    })
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="space-y-4" onSubmit={enviar}>
      <Campo
        para="pp-categoria"
        etiqueta="Categoría"
        ayuda="Una partida viva por categoría: dos topes para el mismo dinero no contestan «¿cuánto queda?»."
      >
        <SelectorBuscable
          id="pp-categoria"
          opciones={(categorias.data ?? [])
            .filter((c) => c.activo)
            .map((c) => ({ valor: c.id, etiqueta: c.nombre, detalle: c.tipo_gasto }))}
          valor={categoriaId}
          onCambiar={setCategoriaId}
          vacio="Elige la categoría"
          placeholder="Nombre de la categoría…"
        />
      </Campo>

      <Campo
        para="pp-monto"
        etiqueta="Tope del mes (USD)"
        ayuda="En dólares, como el informe de gastos. Cada orden se convierte con la tasa de su día de emisión."
      >
        <input
          id="pp-monto"
          name="monto_mensual"
          inputMode="decimal"
          required
          className="campo mt-1 tabular-nums"
        />
      </Campo>

      <Campo para="pp-notas" etiqueta="Notas" ayuda="Opcional: de dónde sale la cifra.">
        <input id="pp-notas" name="notas" className="campo w-full" />
      </Campo>

      <Campo
        para="pp-umbral"
        etiqueta="Avisar a partir de (%)"
        ayuda="Cuando lo usado llegue a este porcentaje del tope, la partida avisa por la campanita y Telegram. No bloquea nada."
      >
        <input
          id="pp-umbral"
          name="umbral_aviso"
          type="number"
          min={1}
          max={100}
          required
          defaultValue={80}
          className="campo mt-1 w-24 tabular-nums"
        />
      </Campo>

      {error !== null && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Boton onClick={onCerrar}>Cancelar</Boton>
        <Boton
          type="submit"
          variante="primario"
          disabled={guardar.isPending || categoriaId === null}
        >
          Crear partida
        </Boton>
      </div>
    </form>
  )
}

/**
 * Ajustar una partida: su tope, su nota, o archivarla.
 *
 * ⚠ **Cambiar el tope vale para TODOS los meses que la partida gobierna**, también los
 * ya cerrados: es un tope con vigencia, no una cifra por mes. Si hace falta que octubre
 * tuviera 400 y noviembre 500, se archiva ésta con `vigente_hasta` y se abre otra — la
 * base sólo admite una viva por categoría, y eso es lo que mantiene la respuesta a
 * «¿cuánto queda?» en una sola cifra.
 *
 * ⚠ **Archivar no borra el histórico**: la partida archivada deja de gobernar meses
 * nuevos y los que gobernó se siguen leyendo igual.
 */
function AjustarPartida({
  partida,
  onCerrar,
  onGuardado,
}: {
  partida: EstadoPartida
  onCerrar: () => void
  onGuardado: () => void
}) {
  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.patch(`/api/v1/presupuesto/partidas/${partida.id}`, cuerpo),
    onSuccess: onGuardado,
  })

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        const campos = new FormData(e.currentTarget)
        const notas = String(campos.get("notas") ?? "").trim()
        guardar.mutate({
          monto_mensual: decimalOpcional(String(campos.get("monto_mensual") ?? "")),
          notas: notas === "" ? null : notas,
          umbral_aviso: Number(campos.get("umbral_aviso") ?? partida.umbral),
        })
      }}
    >
      <Campo
        para="pp-ed-monto"
        etiqueta="Tope del mes (USD)"
        ayuda={`Ahora mismo lleva ${partida.usado} usado de ${partida.tope}.`}
      >
        <input
          id="pp-ed-monto"
          name="monto_mensual"
          inputMode="decimal"
          required
          defaultValue={partida.tope}
          className="campo mt-1 tabular-nums"
        />
      </Campo>

      <Campo para="pp-ed-notas" etiqueta="Notas" ayuda="De dónde sale la cifra.">
        <input
          id="pp-ed-notas"
          name="notas"
          defaultValue={partida.notas ?? ""}
          className="campo w-full"
        />
      </Campo>

      <Campo
        para="pp-ed-umbral"
        etiqueta="Avisar a partir de (%)"
        ayuda="Cuando lo usado llegue a este porcentaje del tope, la partida avisa por la campanita y Telegram. No bloquea nada."
      >
        <input
          id="pp-ed-umbral"
          name="umbral_aviso"
          type="number"
          min={1}
          max={100}
          required
          defaultValue={partida.umbral}
          className="campo mt-1 w-24 tabular-nums"
        />
      </Campo>

      {error !== null && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Boton
          variante="peligro"
          onClick={() => guardar.mutate({ activo: false })}
          disabled={guardar.isPending}
        >
          <PauseCircle size={16} /> Archivar la partida
        </Boton>
        <div className="flex gap-2">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" variante="primario" disabled={guardar.isPending}>
            Guardar
          </Boton>
        </div>
      </div>
    </form>
  )
}

type LineaDePartida = {
  orden_id: number
  codigo: string
  fecha: string
  concepto: string
  proveedor: string | null
  categoria: string | null
  monto: string
  divisa: string
  en_dolares: string
  estado: string
  pagada: boolean
  /** La orden reparte su neto entre varias categorías y ésta es SU parte. */
  parcial?: boolean
  monto_orden?: string
}

type Detalle = {
  partida_id: number
  categoria: string
  tope: string
  lineas: LineaDePartida[]
  sin_convertir: { codigo: string; divisa: string; monto: string; motivo: string }[]
}

/**
 * Qué órdenes consumieron la partida ese mes.
 *
 * ⚠ **Se enseña el importe ORIGINAL y el convertido.** La partida se lleva en dólares,
 * así que una orden en bolívares aporta una cifra que no se parece a la suya: sin las
 * dos columnas, la suma no cuadraría con el total y parecería un error. La tasa es la
 * del día de emisión de cada orden, no la de hoy.
 *
 * ⚠ **Lo pagado y lo comprometido se distinguen**, porque no son lo mismo para decidir:
 * lo primero ya salió del banco y lo segundo todavía se puede parar.
 */
function DetallePartida({
  partida,
  filtro,
  puedeAdministrar,
  onAbrir,
  onGuardado,
}: {
  partida: EstadoPartida
  /** `periodo=…&fecha=…` del selector (28/10). */
  filtro: string
  puedeAdministrar: boolean
  onAbrir: (ordenId: number) => void
  onGuardado: () => void
}) {
  const [ajustando, setAjustando] = useState(false)
  const consulta = useQuery({
    queryKey: ["presupuesto", "partida", partida.id, filtro],
    queryFn: () => api.get<Detalle>(`/api/v1/presupuesto/partidas/${partida.id}/ordenes?${filtro}`),
  })

  if (consulta.isPending) return <p className="py-6">Cargando…</p>
  if (consulta.isError || !consulta.data) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudo cargar el detalle.
      </p>
    )
  }

  const datos = consulta.data

  return (
    <div className="space-y-3">
      {/* ⚠ **El ajuste vive AQUÍ y no en la fila.** Estuvo como un lápiz flotando sobre
          la tarjeta y pisaba la cifra del tope, que es justo lo que hay que leer. Dentro
          del detalle no estorba y queda al lado de lo que explica por qué se ajusta. */}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {comoMonto(partida.usado)} de {comoMonto(partida.tope)} · {datos.lineas.length}{" "}
          {datos.lineas.length === 1 ? "orden" : "órdenes"}
        </p>
        {puedeAdministrar && !ajustando && (
          <Boton tamano="sm" onClick={() => setAjustando(true)}>
            <Pencil size={14} /> Ajustar el tope
          </Boton>
        )}
      </div>

      {ajustando && (
        <div className="tarjeta p-3">
          <AjustarPartida
            partida={partida}
            onCerrar={() => setAjustando(false)}
            onGuardado={onGuardado}
          />
        </div>
      )}

      <Tabla etiqueta={`Órdenes de ${datos.categoria}`}>
        <Cabecera>
          <Th>Orden</Th>
          <Th>Fecha</Th>
          <Th>Descripción</Th>
          <Th derecha>Importe</Th>
          <Th derecha>En dólares</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {datos.lineas.length === 0 && (
            <TablaVacia colSpan={6}>
              Nada consumió esta partida ese mes. Lo que está en borrador todavía no cuenta: se
              edita y se anula sin que nadie lo firme.
            </TablaVacia>
          )}
          {datos.lineas.map((l) => (
            <Fila
              key={`${l.orden_id}-${l.categoria ?? ""}`}
              className="cursor-pointer"
              onClick={() => onAbrir(l.orden_id)}
            >
              <Td>{l.codigo}</Td>
              <Td numero>{comoFecha(l.fecha)}</Td>
              <Td>
                <div>{l.concepto}</div>
                {l.parcial && l.monto_orden && (
                  <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    Parte de una orden de {comoMonto(l.monto_orden)} {l.divisa}: el resto va a otra
                    categoría
                  </div>
                )}
                {l.proveedor !== null && (
                  <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {l.proveedor}
                  </div>
                )}
              </Td>
              <Td derecha numero>
                {comoMonto(l.monto)} <span style={{ color: "var(--fg-muted)" }}>{l.divisa}</span>
              </Td>
              <Td derecha numero>
                {comoMonto(l.en_dolares)}
              </Td>
              <Td>
                <Insignia tono={l.pagada ? "exito" : "info"}>
                  {l.pagada ? "Pagada" : "Comprometida"}
                </Insignia>
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      {datos.sin_convertir.length > 0 && (
        <p className="text-sm" style={{ color: "var(--warning)" }}>
          {datos.sin_convertir.length} orden(es) no se pudieron convertir y quedaron fuera de la
          cuenta: {datos.sin_convertir.map((s) => `${s.codigo} (${s.motivo})`).join(", ")}.
        </p>
      )}
    </div>
  )
}

/**
 * El gasto que no gobierna ninguna partida, orden por orden.
 *
 * ⚠ **Se enseña la CATEGORÍA de cada una**, que es lo primero que se mira aquí: dice si
 * falta una partida, si la orden está mal clasificada o si es gasto que a propósito no
 * se presupuesta —el costo operativo de un expediente, por ejemplo—.
 */
function FueraDePartida({
  filtro,
  onAbrir,
}: {
  filtro: string
  onAbrir: (ordenId: number) => void
}) {
  const consulta = useQuery({
    queryKey: ["presupuesto", "fuera", filtro],
    queryFn: () =>
      api.get<{ lineas: LineaDePartida[]; sin_convertir: Detalle["sin_convertir"] }>(
        `/api/v1/presupuesto/fuera-de-partida?${filtro}`,
      ),
  })

  if (consulta.isPending) return <p className="py-6">Cargando…</p>
  if (consulta.isError || !consulta.data) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudo cargar el detalle.
      </p>
    )
  }

  return (
    <Tabla etiqueta="Gasto fuera de partida">
      <Cabecera>
        <Th>Orden</Th>
        <Th>Fecha</Th>
        <Th>Categoría</Th>
        <Th>Descripción</Th>
        <Th derecha>Importe</Th>
        <Th derecha>En dólares</Th>
      </Cabecera>
      <Cuerpo>
        {consulta.data.lineas.length === 0 && (
          <TablaVacia colSpan={6}>
            Todo el gasto ejecutado de este mes cae dentro de alguna partida.
          </TablaVacia>
        )}
        {consulta.data.lineas.map((l) => (
          <Fila
            key={`${l.orden_id}-${l.categoria ?? ""}`}
            className="cursor-pointer"
            onClick={() => onAbrir(l.orden_id)}
          >
            <Td>{l.codigo}</Td>
            <Td numero>{comoFecha(l.fecha)}</Td>
            <Td>{l.categoria ?? "Sin categoría"}</Td>
            <Td>
              <div>{l.concepto}</div>
              {l.parcial && l.monto_orden && (
                <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  Parte de una orden de {comoMonto(l.monto_orden)} {l.divisa}: el resto va a otra
                  categoría
                </div>
              )}
              {l.proveedor !== null && (
                <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {l.proveedor}
                </div>
              )}
            </Td>
            <Td derecha numero>
              {comoMonto(l.monto)} <span style={{ color: "var(--fg-muted)" }}>{l.divisa}</span>
            </Td>
            <Td derecha numero>
              {comoMonto(l.en_dolares)}
            </Td>
          </Fila>
        ))}
      </Cuerpo>
    </Tabla>
  )
}
